import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { AppLayout } from './components/Layout';
import { AuthGuard } from './components/AuthGuard';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuthStore } from './stores/auth';
import { authApi } from './api/auth';
import { setMessageApi } from './utils/message';

// Code-split heavy pages
const LoginPage = lazy(() => import('./pages/login/LoginPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const AccountListPage = lazy(() => import('./pages/accounts/AccountListPage'));
const AccountDetailPage = lazy(() => import('./pages/accounts/AccountDetailPage'));
const AdminAccountListPage = lazy(() => import('./pages/adminAccounts/AdminAccountListPage'));
const ReviewCenterPage = lazy(() => import('./pages/reviews/ReviewCenterPage'));
const ReviewTemplateListPage = lazy(() => import('./pages/reviewTemplates/ReviewTemplateListPage'));
const AuditLogPage = lazy(() => import('./pages/auditLogs/AuditLogPage'));
const DomainEventsPage = lazy(() => import('./pages/operations/DomainEventsPage'));
const ExperimentList = lazy(() => import('./pages/experiments/ExperimentList'));
const ExperimentDetail = lazy(() => import('./pages/experiments/ExperimentDetail'));
const ExperimentCreate = lazy(() => import('./pages/experiments/ExperimentCreate'));
const ContentReviewQueuePage = lazy(() => import('./pages/contentReview/ContentReviewQueuePage'));
const ContentReviewDetailPage = lazy(() => import('./pages/contentReview/ContentReviewDetailPage'));
const BannerManagementPage = lazy(() => import('./pages/banners/BannerManagementPage'));
const DepositsManagementPage = lazy(() => import('./pages/deposits/DepositsManagementPage'));
const RatingsManagementPage = lazy(() => import('./pages/ratings/RatingsManagementPage'));
const FinanceLeadsPage = lazy(() => import('./pages/finance/FinanceLeadsPage'));
const AnnouncementManagementPage = lazy(() => import('./pages/announcements/AnnouncementManagementPage'));
const CategoryManagementPage = lazy(() => import('./pages/config/CategoryManagementPage'));
const NotificationTemplateManagementPage = lazy(() => import('./pages/config/NotificationTemplateManagementPage'));
const SystemConfigPage = lazy(() => import('./pages/config/SystemConfigPage'));
const ImportManagementPage = lazy(() => import('./pages/imports/ImportManagementPage'));
const SystemHealthPage = lazy(() => import('./pages/operations/SystemHealthPage'));
const ScheduledJobRunsPage = lazy(() => import('./pages/operations/ScheduledJobRunsPage'));
const ContentTakedownPage = lazy(() => import('./pages/content/ContentTakedownPage'));
const ContentAnalyticsPage = lazy(() => import('./pages/analytics/ContentAnalyticsPage'));

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <Spin size="large" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function AppInitializer({ children }: { children: React.ReactNode }) {
  const { setAccount, setIsLoading } = useAuthStore();
  const { message } = AntApp.useApp();

  useEffect(() => {
    setMessageApi(message);
  }, [message]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    authApi.getMe()
      .then((account) => {
        setAccount(account);
      })
      .catch(() => {
        // Token invalid or expired — clear state
        useAuthStore.getState().logout();
      });
  }, [setAccount, setIsLoading]);

  return <>{children}</>;
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider
          locale={zhCN}
          theme={{
            token: {
              colorPrimary: '#1677ff',
            },
          }}
        >
          <AntApp>
            <BrowserRouter>
              <AppInitializer>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route
                      element={
                        <AuthGuard>
                          <AppLayout />
                        </AuthGuard>
                      }
                    >
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/accounts" element={<AccountListPage />} />
                      <Route path="/accounts/:id" element={<AccountDetailPage />} />
                      <Route
                        path="/admin-accounts"
                        element={
                          <AuthGuard requireSuperAdmin>
                            <AdminAccountListPage />
                          </AuthGuard>
                        }
                      />
                      <Route path="/reviews" element={<ReviewCenterPage />} />
                      <Route path="/review-templates" element={<ReviewTemplateListPage />} />
                      <Route path="/audit-logs" element={<AuditLogPage />} />
                      <Route path="/operations/domain-events" element={<DomainEventsPage />} />
                      <Route path="/experiments" element={<ExperimentList />} />
                      <Route path="/experiments/create" element={<ExperimentCreate />} />
                      <Route path="/experiments/:id" element={<ExperimentDetail />} />
                      <Route path="/content-review" element={<ContentReviewQueuePage />} />
                      <Route path="/content-review/:id" element={<ContentReviewDetailPage />} />
                      <Route path="/banners" element={<BannerManagementPage />} />
                      <Route path="/deposits" element={<DepositsManagementPage />} />
                      <Route path="/ratings" element={<RatingsManagementPage />} />
                      <Route path="/finance-leads" element={<FinanceLeadsPage />} />
                      <Route path="/announcements" element={<AnnouncementManagementPage />} />
                      <Route path="/categories" element={<CategoryManagementPage />} />
                      <Route path="/notification-templates" element={<NotificationTemplateManagementPage />} />
                      <Route path="/system-config" element={<SystemConfigPage />} />
                      <Route path="/imports" element={<ImportManagementPage />} />
                      <Route path="/system-health" element={<SystemHealthPage />} />
                      <Route path="/operations/scheduled-job-runs" element={<ScheduledJobRunsPage />} />
                      <Route path="/content-takedown" element={<ContentTakedownPage />} />
                      <Route path="/content-analytics" element={<ContentAnalyticsPage />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Suspense>
              </AppInitializer>
            </BrowserRouter>
          </AntApp>
        </ConfigProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
